import {
  Box,
  Center,
  Flex,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SourcingEventStatus, useListSourcingEventsForBuyerQuery } from "../../autogen/bff-api";
import { BUCKET_META, STATUSES_BY_BUCKET, isBucketKey, type BucketKey } from "../../common/layout/v2/statusBuckets";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { useNavLabel } from "../../common/layout/v2/useNavLabel";
import { useSub } from "../../common/subscription/useSub";
import { urls } from "../../urls";
import { SourcingEventV2StatusTag } from "./sourcing-event/SourcingEventV2StatusTag";

// Filter tabs derive from the same source of truth as the sidebar counts, so a status
// can never be countable-but-unfilterable (or vice versa).
const STATUS_FILTERS: Record<string, { statuses: SourcingEventStatus[]; labelKey: string }> =
  Object.fromEntries(
    (Object.keys(BUCKET_META) as BucketKey[]).map((key) => [
      key,
      { statuses: STATUSES_BY_BUCKET[key], labelKey: BUCKET_META[key].labelKey },
    ]),
  );

export const SourcingEventsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const navLabel = useNavLabel();
  const sub = useSub();
  const [searchParams] = useSearchParams();

  const { data, isLoading, isError } = useListSourcingEventsForBuyerQuery(
    sub.hasSourcingEventV2Access ? {} : skipToken,
  );

  const statusParam = searchParams.get("status");
  const filter = isBucketKey(statusParam) ? STATUS_FILTERS[statusParam] : undefined;
  const allEvents = data?.events ?? [];
  const events = filter ? allEvents.filter((event) => filter.statuses.includes(event.status)) : allEvents;

  if (!sub.hasSourcingEventV2Access) {
    return (
      <Center py="20">
        <Text color="smMuted">{t("Your organization does not have access to this feature.")}</Text>
      </Center>
    );
  }

  return (
    <Box maxW="container.xl" mx="auto">
      <Heading as="h1" size="lg" color="smText" mb={filter ? "6" : "2"}>
        {filter ? navLabel(filter.labelKey) : t("All procurements")}
      </Heading>
      {!filter && (
        <Text color="smMuted" mb="6">
          {t("All your sourcing events.")}
        </Text>
      )}

      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
        {isLoading && (
          <Flex justify="center" align="center" py="16">
            <Spinner color="smPrimary" />
          </Flex>
        )}

        {!isLoading && isError && (
          <Flex justify="center" py="16">
            <Text color="smError">{t("Could not load sourcing events.")}</Text>
          </Flex>
        )}

        {!isLoading && !isError && events.length === 0 && (
          <Flex justify="center" py="16">
            <Text color="smMuted">{t("No sourcing events yet.")}</Text>
          </Flex>
        )}

        {!isLoading && !isError && events.length > 0 && (
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Title")}</Th>
                  <Th>{t("Status")}</Th>
                  <Th>{t("Deadline")}</Th>
                  <Th>{t("Created")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {events.map((event) => (
                  <Tr
                    key={event.id}
                    cursor="pointer"
                    _hover={{ bg: "smBackgroundSecondary" }}
                    onClick={() => navigate(urls.v2.sourcingEvents.view.go(event.id))}
                  >
                    <Td fontWeight="medium" color="smText">
                      {event.title || t("Untitled")}
                    </Td>
                    <Td>
                      <SourcingEventV2StatusTag status={event.status} size="sm" />
                    </Td>
                    <Td color="smMuted">
                      {event.bidSubmissionDeadline ? displayDate(event.bidSubmissionDeadline) : "–"}
                    </Td>
                    <Td color="smMuted">{displayDate(event.createdAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {!isLoading && !isError && events.length > 0 && (
        <Text color="smMuted" fontSize="sm" mt="3">
          {events.length} {t("results")}
        </Text>
      )}
    </Box>
  );
};
